import React, { type FC, type PropsWithChildren } from 'react';

import { withProduct } from '@lucid/utils';

import Text from '../../text/text';
import Box from '../../box/box';
import Image from '../../image/image';
import Card, { type CardElementProps } from '../../card/card';

export interface TemplateCardProps extends Omit<CardElementProps, 'tags' | 'text' | 'children' | 'divider'> {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Checks if the TemplateCard is the hub link. */
  isTemplateHubLink?: boolean;
  /** Label above the integration icons */
  integrationText?: string;
  /** Product ID for the integration icons */
  productId?: number;
}

const TemplateCard: FC<PropsWithChildren<TemplateCardProps>> = ({
  className,
  link,
  media,
  heading,
  isTemplateHubLink,
  integrationText,
  productId,
}) => {
  const product = withProduct(productId || 0);
  const integrationImage = {
    chart: 'https://corporate-assets.lucid.co/co/2e8149c5-2a9f-4d67-9b91-5ae21d2a07d7.png',
    spark: 'https://corporate-assets.lucid.co/co/5d061930-cb8b-4b4a-989e-78356e0806c6.png',
  }[product || 'chart'];

  return (
    <Card
      isFullCardLink
      shouldAnimateOnHover
      css={theme => ({
        label: 'templateCard',
        height: '100%',
        width: '100%',
        backgroundColor: isTemplateHubLink ? theme.colors.primaryTertiaryAccent : theme.colors.white,
        padding: `${theme.space.s32}px ${theme.space.s24}px`,
        '[data-card-element="heading"]': {
          display: '-webkit-box',
          WebkitLineClamp: 2,
          // required so that descenders don't get cut off
          paddingBottom: '0.1em',
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        },
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
      data-testid="template-card-container"
    >
      {media && (
        <Card.Media
          {...media}
          css={theme => isTemplateHubLink && { backgroundColor: theme.colors.primaryTertiaryAccent }}
        />
      )}
      {media && <Card.Divider type={isTemplateHubLink ? 'fullBleedPrimary' : 'fullBleed'} />}
      {heading && <Card.Heading as="h3" heading={heading} variant={isTemplateHubLink ? 'headingS' : 'headingM'} />}
      {integrationText && (
        <Box>
          <Text css={theme => ({ marginBottom: theme.space.s4 })} variant="label2XS400">
            {integrationText}
          </Text>
          <Box>
            <Image
              css={theme => ({ paddingRight: theme.space.s16, height: '30px' })}
              imageAlt=""
              imageSrc={integrationImage}
            />
          </Box>
        </Box>
      )}
      {link?.final_url && <Card.Link {...link} isFullCardLink />}
    </Card>
  );
};

export { TemplateCard };
export default TemplateCard;
